import * as Phaser from 'phaser';
import { ProceduralSpriteFactory } from '@/factories/ProceduralSpriteFactory';
import { GAME } from '@/config/GameConfig';
import { onComputeChange } from '@/net/PlayroomBridge';

/**
 * Command Hub — passive resource farm. One Terafab in the centre, ambient
 * parallax starfield, a looping compute tick telegraph.
 *
 * Pressing the FRONT tab (DOM) swaps to BattleFrontScene.
 */
export class CommandHubScene extends Phaser.Scene {
  private factory!: ProceduralSpriteFactory;
  private computeText!: Phaser.GameObjects.Text;
  private unsubCompute?: () => void;

  constructor() {
    super('CommandHubScene');
  }

  create(): void {
    const { width, height } = this.scale;
    this.factory = new ProceduralSpriteFactory(this);
    this.cameras.main.setBackgroundColor('#050505');

    drawStarfield(this);
    drawGroundGrid(this, width, height);

    // Terafab building, centered, slightly above midline.
    this.factory.terafab(width / 2, height * 0.58);

    // Label plate.
    this.add
      .text(width / 2, height * 0.58 + 180, 'TERAFAB // COMPUTE FOUNDRY', {
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '18px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setLetterSpacing?.(8);

    this.add
      .text(width / 2, height * 0.58 + 210, `+${GAME.COMPUTE_PER_TICK} COMPUTE / 60s`, {
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '13px',
        color: '#9a9aa0',
      })
      .setOrigin(0.5);

    // Live compute readout inside the Phaser canvas (DOM HUD mirrors this).
    this.computeText = this.add
      .text(width / 2, 120, 'COMPUTE 000000', {
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '28px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    this.computeText.setLetterSpacing?.(6);

    this.unsubCompute = onComputeChange((n) => {
      this.computeText?.setText(`COMPUTE ${n.toString().padStart(6, '0')}`);
    });

    // Demo: a friendly starship drifting on parallax above the fab.
    const hero = this.factory.starship(width / 2, height * 0.3, 'friendly');
    this.tweens.add({
      targets: hero.container,
      y: height * 0.3 - 10,
      duration: 2600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unsubCompute?.());
  }
}

// --- Background helpers ---------------------------------------------------

function drawStarfield(scene: Phaser.Scene): void {
  const g = scene.add.graphics();
  const { width, height } = scene.scale;
  for (let i = 0; i < 140; i++) {
    const x = Math.random() * width;
    const y = Math.random() * height;
    const r = Math.random() < 0.92 ? 1 : 2;
    const a = 0.2 + Math.random() * 0.7;
    g.fillStyle(0xffffff, a);
    g.fillCircle(x, y, r);
  }
}

function drawGroundGrid(scene: Phaser.Scene, w: number, h: number): void {
  const g = scene.add.graphics();
  g.lineStyle(1, 0xffffff, 0.08);
  const horizon = h * 0.72;
  // Horizon line.
  g.beginPath();
  g.moveTo(0, horizon);
  g.lineTo(w, horizon);
  g.strokePath();
  // Receding verticals (fake perspective).
  const cx = w / 2;
  for (let i = -10; i <= 10; i++) {
    g.beginPath();
    g.moveTo(cx + i * (w / 6), h);
    g.lineTo(cx + i * 12, horizon);
    g.strokePath();
  }
  // Horizontals.
  for (let i = 1; i <= 8; i++) {
    const y = horizon + (i * i) * (h * 0.004);
    if (y > h) break;
    g.beginPath();
    g.moveTo(0, y);
    g.lineTo(w, y);
    g.strokePath();
  }
}
